import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ColumnConfig } from '../dashboard/table-config';
import { MessageService, MenuItem } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TemplateRegistryService } from '../../services/template-registry.service';
import { Template } from '../../models/template.model';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { escapeHtml } from '../../../core/validators/no-special-chars.validator';

@Component({
  selector: 'app-templates-registry',
  standalone: true,
  imports: [CommonModule, RegistryTableComponent, Toast, BreadcrumbModule, ButtonModule, ConfirmDialogModule],
  providers: [MessageService, ConfirmationService],
  templateUrl: './templates-registry.component.html',
  styleUrls: ['./templates-registry.component.scss'],
})
export class TemplatesRegistryComponent implements OnInit {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private templateRegistryService = inject(TemplateRegistryService);
  private navigationContext = inject(NavigationContextService);
  private titleService = inject(Title);

  // State
  templatesData = signal<any[]>([]);
  isLoading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([]);
  showExport = true;
  showClearFilters = true;
  emptyMessage = signal<string>('Non ci sono template da visualizzare. Crea il tuo primo template!');

  private get contractTemplatesRoute(): string {
    return `/axa/${this.navigationContext.getDomainPrefix()}-contract-templates`;
  }

  // Column configuration
  columns: ColumnConfig[] = [
    { field: 'id', title: 'ID', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca ID' },
    { field: 'name', title: 'Nome Template', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    { field: 'description', title: 'Descrizione', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca descrizione' },
    { field: 'sectionsCount', title: 'N. Sezioni', type: 'text', sortable: true, filterType: 'text' },
    { field: 'createdDate', title: 'Data Creazione', type: 'text', sortable: true, filterType: 'date' },
    { field: 'lastModified', title: 'Ultima Modifica', type: 'text', sortable: true, filterType: 'date' },
    { field: 'status', title: 'Stato', type: 'tag', sortable: true, filterType: 'select', filterOptions: [
      { label: 'Bozza', value: 'DRAFT' },
      { label: 'Attivo', value: 'ACTIVE' },
      { label: 'Inattivo', value: 'INACTIVE' }
    ]},
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  ngOnInit(): void {
    this.titleService.setTitle('Registro Template - PalEasy');
    // Inizializza breadcrumb
    this.breadcrumbItems.set([
      { label: 'Registro Template' }
    ]);

    this.loadTemplates();
  }

  loadTemplates(): void {
    this.isLoading.set(true);
    
    // Determina il domainType in base al contesto di navigazione
    const domainType = this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
    
    this.templateRegistryService.getTemplates(domainType).subscribe({
      next: (response) => {
        if (response?.status && Array.isArray(response.data)) {
          // Mappiamo i dati per aggiungere i campi calcolati
          const mappedData = response.data.map((template: Template) => ({
            ...template,
            sectionsCount: template.sections?.length || 0,
            createdDate: template.createdAt ? new Date(template.createdAt).toLocaleDateString('it-IT') : '-',
            lastModified: template.updatedAt ? new Date(template.updatedAt).toLocaleDateString('it-IT') : '-',
            status: template.status || 'DRAFT'
          }));
          this.templatesData.set(mappedData);
        } else {
          this.templatesData.set([]);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading templates:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare la lista dei template'
        });
        this.isLoading.set(false);
        this.templatesData.set([]);
      }
    });
  }

  onDetailClick(template: any): void {
    console.log('Visualizza dettaglio template:', template);
    // Naviga al dettaglio template (stesso di edit)
    this.router.navigate([this.contractTemplatesRoute], {
      queryParams: { id: template.id } 
    });
  }

  createNewTemplate(): void {
    // Naviga alla pagina di creazione nuovo template
    this.router.navigate([this.contractTemplatesRoute], {
      queryParams: { new: 'true' } 
    });
  }

  onEditClick(template: any): void {
    console.log('Modifica template:', template);
    // Naviga alla pagina di modifica template
    this.router.navigate([this.contractTemplatesRoute], {
      queryParams: { id: template.id } 
    });
  }

  onDeleteClick(template: any): void {
    console.log('Elimina template:', template);
    
    // Conferma eliminazione
    this.confirmationService.confirm({
      message: `Sei sicuro di voler eliminare il template "${escapeHtml(template.name)}"?`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.templateRegistryService.deleteTemplate(template.id).subscribe({
          next: (response) => {
            if (response?.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Template Eliminato',
                detail: `Il template "${template.name}" è stato eliminato con successo.`,
                life: 3000
              });
              this.loadTemplates(); // Ricarica la lista
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: response?.message || 'Impossibile eliminare il template'
              });
            }
          },
          error: (err) => {
            console.error('Error deleting template:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare il template'
            });
          }
        });
      }
    });
  }

  onDownloadClick(template: any): void {
    console.log('Download template:', template);
    this.messageService.add({
      severity: 'info',
      summary: 'Download in corso',
      detail: `Download del template "${template.name}" avviato.`,
      life: 3000
    });
  }

  onClearFilters(): void {
    console.log('Filtri azzerati');
    this.loadTemplates();
  }

  onExport(): void {
    console.log('Esportazione dati template');
    this.messageService.add({
      severity: 'info',
      summary: 'Esportazione',
      detail: 'Esportazione della lista template avviata.',
      life: 3000
    });
  }
}
